import { useCallback } from 'react';

// Extend Window interface for GTM dataLayer
declare global {
  interface Window {
    dataLayer?: any[];
  }
}

export const useGTMTracking = () => {
  const trackEvent = useCallback((eventName: string, parameters: Record<string, any> = {}) => {
    try {
      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
          event: eventName,
          ...parameters,
        });
      }
    } catch (error) {
      console.warn('GTM tracking failed:', error);
    }
  }, []);

  const trackPageView = useCallback((pagePath: string, pageTitle: string) => {
    trackEvent('page_view', {
      page_path: pagePath,
      page_title: pageTitle,
    });
  }, [trackEvent]);

  const trackButtonClick = useCallback((buttonName: string, section?: string) => {
    trackEvent('button_click', {
      button_name: buttonName,
      section: section || 'unknown',
    });
  }, [trackEvent]);

  const trackFormSubmission = useCallback((formName: string, success: boolean = true) => {
    trackEvent('form_submission', {
      form_name: formName,
      success: success,
    });
  }, [trackEvent]);

  const trackScrollDepth = useCallback((percentage: number) => {
    trackEvent('scroll_depth', {
      scroll_depth: percentage,
    });
  }, [trackEvent]);

  const trackFormStart = useCallback((formName: string) => {
    trackEvent('form_start', {
      form_name: formName,
      timestamp: new Date().toISOString(),
    });
  }, [trackEvent]);

  const trackFormValidationError = useCallback((formName: string, errorFields: string[]) => {
    trackEvent('form_validation_error', {
      form_name: formName,
      error_fields: errorFields,
      error_count: errorFields.length,
    });
  }, [trackEvent]);

  const trackFormCompletion = useCallback((formName: string, completionTime: number) => {
    trackEvent('form_completion', {
      form_name: formName,
      completion_time_seconds: completionTime,
      success: true,
    });
  }, [trackEvent]);

  const trackFormAbandonment = useCallback((formName: string, lastField: string, timeSpent: number) => {
    trackEvent('form_abandonment', {
      form_name: formName,
      last_field: lastField,
      time_spent_seconds: timeSpent,
    });
  }, [trackEvent]);

  const trackContactInteraction = useCallback((interactionType: string, details?: Record<string, any>) => {
    trackEvent('contact_interaction', {
      interaction_type: interactionType,
      ...details,
    });
  }, [trackEvent]);

  const trackLeadQuality = useCallback((leadData: {
    form_name: string;
    interest_level: string;
    company_size?: string;
    urgency?: string;
  }) => {
    trackEvent('lead_qualification', leadData);
  }, [trackEvent]);

  return {
    trackEvent,
    trackPageView,
    trackButtonClick,
    trackFormSubmission,
    trackScrollDepth,
    trackFormStart,
    trackFormValidationError,
    trackFormCompletion,
    trackFormAbandonment,
    trackContactInteraction,
    trackLeadQuality,
  };
};